import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileTextIcon, PencilIcon, SparklesIcon } from 'lucide-react';
import { Button, EmptyState, ErrorNotice, FactList, LoadingState, Panel, Pill, type Fact } from '@bcecloud/portal-ui';
import { toast } from 'sonner';
import { NoteBody } from '@/components/NoteBody';
import { RichTextEditor } from '@/components/RichTextEditor';
import { errorMessage } from '@/lib/api';
import {
  draftEventSummary,
  noteCategoryLabel,
  readEventSummary,
  readMyIdentity,
  saveEventSummary,
  type EventNote,
  type EventPerson,
} from '@/lib/events';
import { formatTemplateValue, listTemplates, templateStatusTone, type Template } from '@/lib/templates';

/** A person's name from a populated ref, or nothing. */
function personName(ref: EventPerson | string | undefined): string | undefined {
  if (typeof ref !== 'object' || ref === null) return undefined;
  return [ref.firstName, ref.lastName].filter(Boolean).join(' ') || ref.email;
}

/** A note's values as labelled facts, in the template's order. */
function noteFacts(note: EventNote, template: Template | undefined): Fact[] {
  const values = note.values ?? {};
  const keys = [
    ...(template?.fields.map((field) => field.key) ?? []),
    ...Object.keys(values).filter((key) => !template?.fields.some((field) => field.key === key)),
  ];
  return keys.flatMap((key) => {
    const field = template?.fields.find((candidate) => candidate.key === key);
    const value = formatTemplateValue(field, values[key]);
    return value === undefined ? [] : [{ label: field?.label ?? key, value }];
  });
}

/**
 * The event at a glance: the latest structured note per category (charity,
 * production, …) and one free-text summary the program manager owns, with
 * an assistant draft to start from. Facilitators read; internal roles write.
 *
 * @param props - The event id.
 * @returns The summary tab.
 */
export function SummaryTab({ eventId }: { readonly eventId: string }): React.ReactElement {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const identity = useQuery({
    queryKey: ['my-identity'],
    queryFn: ({ signal }) => readMyIdentity(signal),
    staleTime: 5 * 60_000,
  });
  const canEdit = ['internal', 'admin', 'superadmin'].includes(identity.data?.role ?? '');

  const summary = useQuery({
    queryKey: ['event-summary', eventId],
    queryFn: ({ signal }) => readEventSummary(eventId, signal),
  });
  const templates = useQuery({
    queryKey: ['templates'],
    queryFn: ({ signal }) => listTemplates(signal),
    staleTime: 5 * 60_000,
  });

  const [editing, setEditing] = useState(false);
  const [html, setHtml] = useState('');
  // The editor reads its initial HTML once; a new seed remounts it with a draft.
  const [editorSeed, setEditorSeed] = useState(0);
  const [seedHtml, setSeedHtml] = useState('');

  function startEditing(): void {
    const current = summary.data?.summary.html ?? '';
    setSeedHtml(current);
    setHtml(current);
    setEditorSeed((seed) => seed + 1);
    setEditing(true);
  }

  const save = useMutation({
    mutationFn: () => saveEventSummary(eventId, html),
    onSuccess: async () => {
      toast.success('Summary saved');
      setEditing(false);
      await queryClient.invalidateQueries({ queryKey: ['event-summary', eventId] });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const draft = useMutation({
    mutationFn: () => draftEventSummary(eventId),
    onSuccess: (drafted) => {
      setSeedHtml(drafted);
      setHtml(drafted);
      setEditorSeed((seed) => seed + 1);
      toast.success('Draft ready — edit it, then save');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (summary.isPending) return <LoadingState label="Loading summary…" />;
  if (summary.isError) return <ErrorNotice title="Summary unavailable" message={errorMessage(summary.error)} />;

  const { pinned } = summary.data;
  const text = summary.data.summary;
  const updatedBy = personName(text.updatedBy);
  const updatedAt = text.updatedAt ? new Date(text.updatedAt).toLocaleDateString() : undefined;
  const templateFor = (note: EventNote): Template | undefined =>
    note.templateKey ? templates.data?.find((template) => template.key === note.templateKey) : undefined;

  return (
    <div className="space-y-6">
      <Panel className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-portal-card-title font-bold">Event summary</h2>
            <p className="mt-0.5 text-portal-micro text-portal-muted">
              {updatedBy || updatedAt
                ? `Updated${updatedBy ? ` by ${updatedBy}` : ''}${updatedAt ? ` on ${updatedAt}` : ''}`
                : 'The state of the event in a few paragraphs, for whoever opens it next.'}
            </p>
          </div>
          {canEdit && !editing ? (
            <Button variant="secondary" size="compact" onClick={startEditing}>
              <PencilIcon aria-hidden className="size-4" /> {text.html ? 'Edit' : 'Write summary'}
            </Button>
          ) : null}
        </div>

        {editing ? (
          <div className="mt-4 space-y-4">
            <RichTextEditor key={editorSeed} ariaLabel="Event summary" initialHtml={seedHtml} onChange={setHtml} />
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Button variant="secondary" onClick={() => draft.mutate()} disabled={draft.isPending || save.isPending}>
                <SparklesIcon aria-hidden className="size-4" /> {draft.isPending ? 'Drafting…' : 'Draft with AI'}
              </Button>
              <span className="flex items-center gap-3">
                <Button variant="secondary" onClick={() => setEditing(false)} disabled={save.isPending}>
                  Cancel
                </Button>
                <Button onClick={() => save.mutate()} disabled={save.isPending || draft.isPending}>
                  {save.isPending ? 'Saving…' : 'Save summary'}
                </Button>
              </span>
            </div>
            <p className="text-portal-micro text-portal-muted">
              The draft reads the event's facts, notes, shipments and checklist. Nothing is saved until you click Save.
            </p>
          </div>
        ) : text.html ? (
          <NoteBody body={text.html} className="note-prose mt-4 text-portal-small" />
        ) : (
          <div className="mt-4">
            <EmptyState
              icon={<FileTextIcon aria-hidden className="size-6" />}
              title="No summary yet"
              message={
                canEdit
                  ? 'Write one, or let the assistant draft it from the notes and shipments.'
                  : 'The program manager has not written one yet.'
              }
              {...(canEdit ? { action: <Button onClick={startEditing}>Write summary</Button> } : {})}
            />
          </div>
        )}
      </Panel>

      <div>
        <h3 className="mb-2 text-portal-micro font-extrabold tracking-[1px] text-portal-muted uppercase">Latest by category</h3>
        {pinned.length === 0 ? (
          <Panel>
            <EmptyState
              icon={<FileTextIcon aria-hidden className="size-6" />}
              title="No structured notes yet"
              message="Notes written with a template — a charity, a custom program brief — show their status and facts here."
              action={
                <Button variant="secondary" onClick={() => navigate(`/events/${eventId}?tab=notes`)}>
                  Go to notes
                </Button>
              }
            />
          </Panel>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {pinned.map(({ category, note }) => (
              <Panel key={note._id} className="p-4">
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5">
                    <Pill tone="brand">{noteCategoryLabel(category)}</Pill>
                    {note.status ? <Pill tone={templateStatusTone(note.status)}>{note.status}</Pill> : null}
                  </span>
                  <Link to={`/events/${eventId}?tab=notes`} className="text-portal-micro font-bold text-brand hover:underline">
                    Open in notes
                  </Link>
                </div>
                <h4 className="mt-2 text-portal-small font-bold">{note.subject}</h4>
                <div className="mt-3">
                  <FactList facts={noteFacts(note, templateFor(note))} columns={2} />
                </div>
              </Panel>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
